import React, { useState, useEffect, useRef, useContext, useCallback } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { head } from "lodash";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import { isDarkTheme } from "../../helpers/darkTint";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import Typography from "@material-ui/core/Typography";

import { i18n } from "../../translate/i18n";
import moment from "moment";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import {
  SMSBARATO_MAX_LENGTH,
  estimateSmsSegments,
  sanitizeSmsBaratoText,
  sanitizeSmsDevText,
} from "../../utils/smsSegments";
import {
  Box,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel,
  Divider,
} from "@material-ui/core";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SocketContext } from "../../context/Socket/SocketContext";
import ConfirmationModal from "../ConfirmationModal";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
    backgroundColor: isDarkTheme(theme) ? theme.palette.background.paper : "#fff",
  },

  tabmsg: {
    backgroundColor: theme.palette.campaigntab,
  },

  textField: {
    marginRight: theme.spacing(1),
    flex: 1,
  },

  extraAttr: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },

  btnWrapper: {
    position: "relative",
  },

  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },

  sectionTitle: {
    fontWeight: 600,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },

  templateInfo: {
    backgroundColor: isDarkTheme(theme) ? theme.palette.action.hover : "#f5f5f5",
    padding: theme.spacing(1),
    borderRadius: 4,
    marginTop: theme.spacing(1),
    fontSize: "0.85rem",
    color: isDarkTheme(theme) ? theme.palette.text.secondary : "#555",
    whiteSpace: "pre-wrap",
  },
}));

const CampaignSchema = Yup.object()
  .shape({
    name: Yup.string()
      .min(2, () => i18n.t("validation.tooShort"))
      .max(50, () => i18n.t("validation.tooLong"))
      .required(() => i18n.t("validation.required")),
    responseQueueId: Yup.mixed().nullable(),
    responseWindowMinutes: Yup.number()
      .transform((value, originalValue) => {
        if (originalValue === "" || originalValue === null || originalValue === undefined) {
          return null;
        }
        return value;
      })
      .nullable()
      .integer(() => i18n.t("campaigns.dialog.validation.integerMinutes"))
      .min(1, () => i18n.t("campaigns.dialog.validation.minWindow"))
      .max(10080, () => i18n.t("campaigns.dialog.validation.maxWindow")),
  })
  .test(
    "response-routing-pair",
    () => i18n.t("campaigns.dialog.validation.queueAndWindow"),
    function (values) {
      const queueId = values?.responseQueueId;
      const windowMinutes = values?.responseWindowMinutes;

      const hasQueue = queueId !== null && queueId !== undefined && `${queueId}` !== "";
      const hasWindow = windowMinutes !== null && windowMinutes !== undefined && `${windowMinutes}` !== "";

      if ((hasQueue && !hasWindow) || (!hasQueue && hasWindow)) {
        return this.createError({
          path: hasQueue ? "responseWindowMinutes" : "responseQueueId",
          message: hasQueue
            ? i18n.t("campaigns.dialog.validation.windowRequired")
            : i18n.t("campaigns.dialog.validation.responseQueueRequired")
        });
      }

      return true;
    }
  );

/**
 * Extrai variáveis do template ({{1}}, {{nome}}, etc.) de todos os componentes
 */
const extractTemplateVariables = (template) => {
  const variables = [];
  
  if (!template || !template.components) return variables;
  
  // Processa cada componente do template
  template.components.forEach((comp) => {
    // BODY: extrai variáveis do texto
    if (comp.type === "BODY" && comp.text) {
      // Regex para capturar {{1}}, {{2}}, {{nome}}, etc.
      const matches = comp.text.match(/\{\{(\w+)\}\}/g);
      if (matches) {
        matches.forEach((match) => {
          const varName = match.replace(/\{\{|\}\}/g, "");
          if (!variables.find(v => v.key === varName)) {
            variables.push({
              key: varName,
              component: "BODY",
              example: comp.example?.body_text?.[0]?.[variables.filter(v => v.component === "BODY").length] || ""
            });
          }
        });
      }
    }
    
    // HEADER: verifica se tem variável no texto ou mídia
    if (comp.type === "HEADER") {
      if (comp.format === "TEXT" && comp.text) {
        const matches = comp.text.match(/\{\{(\w+)\}\}/g);
        if (matches) {
          matches.forEach((match) => {
            const varName = match.replace(/\{\{|\}\}/g, "");
            if (!variables.find(v => v.key === varName)) {
              variables.push({
                key: varName,
                component: "HEADER",
                format: "TEXT",
                example: comp.example?.header_text?.[0] || ""
              });
            }
          });
        }
      } else if (["IMAGE", "VIDEO", "DOCUMENT"].includes(comp.format)) {
        // Header com mídia (geralmente não tem variável de texto, mas pode ter mídia dinâmica)
        // Por enquanto não tratamos mídia dinâmica em campanhas
      }
    }
    
    // BUTTONS: verifica se tem variáveis em botões dinâmicos (URL, etc.)
    if (comp.type === "BUTTONS" && Array.isArray(comp.buttons)) {
      comp.buttons.forEach((btn, btnIdx) => {
        if (btn.type === "URL" && btn.url && btn.url.includes("{{")) {
          const matches = btn.url.match(/\{\{(\w+)\}\}/g);
          if (matches) {
            matches.forEach((match) => {
              const varName = match.replace(/\{\{|\}\}/g, "");
              if (!variables.find(v => v.key === varName)) {
                variables.push({
                  key: varName,
                  component: "BUTTON",
                  buttonIndex: btnIdx,
                  buttonType: "URL",
                  example: ""
                });
              }
            });
          }
        }
      });
    }
  });
  
  return variables;
};

const CampaignModal = ({
  open,
  onClose,
  campaignId,
  initialValues,
  onSave,
  resetPagination,
}) => {
  const classes = useStyles();
  const isMounted = useRef(true);
  const { user } = useContext(AuthContext);
  const socketManager = useContext(SocketContext);
  const { companyId } = user;
  const [file, setFile] = useState([]);
  const formikRef = useRef(null);
  const [queues, setQueues] = useState([]);

  const initialState = {
    name: "",
    dispatchType: "immediate",
    message: "",
    templateId: "",
    templateVariablesMapping: {}, // Mapeamento de variáveis do template
    windowAction: "template_only",
    sendMode: "message_or_template",
    status: "INATIVA",
    confirmation: false,
    scheduledAt: "",
    whatsappId: "",
    contactListId: "",
    tagListId: "",
    fileListId: "",
    responseQueueId: "",
    responseWindowMinutes: "",
    companyId,
  };

  const [campaign, setCampaign] = useState(initialState);
  const [whatsapps, setWhatsapps] = useState([]);
  const [whatsappsLoaded, setWhatsappsLoaded] = useState(false);
  const [contactLists, setContactLists] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [attachment, setAttachment] = useState(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [campaignEditable, setCampaignEditable] = useState(true);
  const attachmentFile = useRef(null);
  const [tagLists, setTagLists] = useState([]);
  const [whatsappIdForTemplates, setWhatsappIdForTemplates] = useState("");
  const [wabaIdForTemplates, setWabaIdForTemplates] = useState("");
  const [currentTemplateId, setCurrentTemplateId] = useState("");

  const loadContactLists = useCallback(async () => {
    try {
      const { data } = await api.get(`/contact-lists/list`, { params: { companyId } });
      if (isMounted.current) {
        setContactLists(data || []);
      }
    } catch (err) {
      toastError(err);
    }
  }, [companyId]);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/files/", {
          params: { companyId },
        });
        setFile(data.files || []);
      } catch (err) {
        toastError(err);
      }
    })();
  }, [companyId]);

  useEffect(() => {
    if (isMounted.current) {
      if (initialValues) {
        setCampaign((prevState) => {
          return { ...prevState, ...initialValues };
        });
      }

      loadContactLists();

      api
        .get(`/whatsapp`, { params: { companyId, session: 0 } })
        .then(({ data }) => {
          setWhatsapps(data);
          setWhatsappsLoaded(true);
        });
      api
        .get(`/queue`, { params: { companyId } })
        .then(({ data }) => setQueues(data || []));

      api
        .get(`/tags`, { params: { companyId } })
        .then(({ data }) => {
          const fetchedTags = data.tags;
          const formattedTagLists = fetchedTags.map((tag) => ({
            id: tag.id,
            name: tag.name,
          }));
          setTagLists(formattedTagLists);
        })
        .catch((error) => {
          console.error("Error retrieving tags:", error);
        });

      if (!campaignId) return;

      api.get(`/campaigns/${campaignId}`).then(({ data }) => {
        setCampaign((prev) => {
          let prevCampaignData = Object.assign({}, prev);

          Object.entries(data).forEach(([key, value]) => {
            if (key === "scheduledAt" && value !== "" && value !== null) {
              prevCampaignData[key] = moment(value).format("YYYY-MM-DDTHH:mm");
            } else {
              prevCampaignData[key] = value === null ? "" : value;
            }
          });

          // Fallback: se não tem "message" novo, usa message1 (legado)
          if (!prevCampaignData.message && prevCampaignData.message1) {
            prevCampaignData.message = prevCampaignData.message1;
          }

          const scheduledDateValue = prevCampaignData.scheduledAt
            ? moment(prevCampaignData.scheduledAt)
            : null;

          prevCampaignData.dispatchType =
            scheduledDateValue && scheduledDateValue.isValid() && scheduledDateValue.isAfter(moment())
              ? "scheduled"
              : "immediate";

          if (!prevCampaignData.sendMode) {
            prevCampaignData.sendMode = "message_or_template";
          }

          if (!prevCampaignData.windowAction) {
            prevCampaignData.windowAction = "template_only";
          }

          return prevCampaignData;
        });
        // Inicializa whatsappIdForTemplates com o valor da campanha carregada
        if (data.whatsappId) {
          setWhatsappIdForTemplates(data.whatsappId);
          // Resolver wabaId a partir da conexão
          const conn = whatsapps.find(w => w.id === Number(data.whatsappId));
          if (conn?.cloudApiBusinessAccountId) {
            setWabaIdForTemplates(conn.cloudApiBusinessAccountId);
          }
        }
        if (data.templateId) {
          setCurrentTemplateId(data.templateId);
        }
      });
    }
  }, [campaignId, open, initialValues, companyId, loadContactLists]);

  // Campanha de uma conexão excluída (o /whatsapp não devolve as excluídas): a
  // conexão não pode seguir escondida no formulário e voltar no submit.
  useEffect(() => {
    if (!open || !whatsappsLoaded || !campaign?.whatsappId) return;
    if (!whatsapps.some((w) => Number(w.id) === Number(campaign.whatsappId))) {
      setCampaign((prev) => ({ ...prev, whatsappId: "" }));
    }
  }, [open, whatsappsLoaded, whatsapps, campaign?.whatsappId]);

  useEffect(() => {
    if (!open) return;

    const socket = socketManager.getSocket(companyId);

    const handleContactListRefresh = () => {
      loadContactLists();
    };

    socket.on(`company-${companyId}-ContactList`, handleContactListRefresh);
    socket.on(`company-${companyId}-ContactListItem`, handleContactListRefresh);

    return () => {
      socket.off(`company-${companyId}-ContactList`, handleContactListRefresh);
      socket.off(`company-${companyId}-ContactListItem`, handleContactListRefresh);
    };
  }, [open, companyId, socketManager, loadContactLists]);

  // Carrega templates quando wabaId muda (resolve via whatsappId)
  useEffect(() => {
    const wabaId = wabaIdForTemplates;
    if (wabaId) {
      api
        .get(`/message-templates`, {
          params: { wabaId },
        })
        .then(({ data }) => {
          // Filtra apenas templates APPROVED
          const approved = data.filter(
            (t) => t.status === "APPROVED" || t.status === "approved"
          );
          setTemplates(approved);
        })
        .catch((err) => {
          console.error("Erro ao carregar templates:", err);
          setTemplates([]);
        });
    } else if (whatsappIdForTemplates) {
      // Fallback: usar whatsappId (retrocompatível)
      api
        .get(`/message-templates`, {
          params: { whatsappId: whatsappIdForTemplates },
        })
        .then(({ data }) => {
          const approved = data.filter(
            (t) => t.status === "APPROVED" || t.status === "approved"
          );
          setTemplates(approved);
        })
        .catch((err) => {
          console.error("Erro ao carregar templates:", err);
          setTemplates([]);
        });
    } else {
      setTemplates([]);
    }
  }, [wabaIdForTemplates, whatsappIdForTemplates]);

  // Atualiza template selecionado quando templateId muda
  useEffect(() => {
    if (currentTemplateId && templates.length > 0) {
      const found = templates.find(
        (t) => t.id === Number(currentTemplateId)
      );
      setSelectedTemplate(found || null);
      
      // Inicializa mapeamento de variáveis se template selecionado
      // Garante que TODAS as variáveis (BODY, HEADER, BUTTONS) tenham mapeamento
      if (found && formikRef.current) {
        const existingMapping = formikRef.current.values.templateVariablesMapping || {};
        const templateVars = extractTemplateVariables(found);
        
        if (templateVars.length > 0) {
          const newMapping = { ...existingMapping };
          let changed = false;
          templateVars.forEach(varInfo => {
            if (!newMapping[varInfo.key]) {
              // Default: "contact" → "name" para BODY/HEADER, "fixed" → "" para BUTTON
              if (varInfo.component === "BUTTON") {
                newMapping[varInfo.key] = { type: "fixed", value: "" };
              } else {
                newMapping[varInfo.key] = { type: "contact", field: "name" };
              }
              changed = true;
            }
          });
          // Remove variáveis que não existem mais no template
          Object.keys(newMapping).forEach(key => {
            if (!templateVars.find(v => v.key === key)) {
              delete newMapping[key];
              changed = true;
            }
          });
          if (changed) {
            formikRef.current.setFieldValue("templateVariablesMapping", newMapping);
          }
        }
      }
    } else {
      setSelectedTemplate(null);
    }
  }, [currentTemplateId, templates]);

  useEffect(() => {
    const now = moment();
    const scheduledAt = moment(campaign.scheduledAt);
    const moreThenAnHour =
      !Number.isNaN(scheduledAt.diff(now)) &&
      scheduledAt.diff(now, "hour") > 1;
    const isEditable =
      campaign.status === "INATIVA" ||
      (campaign.status === "PROGRAMADA" && moreThenAnHour);

    setCampaignEditable(isEditable);
  }, [campaign.status, campaign.scheduledAt]);

  const handleClose = () => {
    onClose();
    setCampaign(initialState);
    setSelectedTemplate(null);
  };

  const handleAttachmentFile = (e) => {
    const file = head(e.target.files);
    if (file) {
      setAttachment(file);
    }
  };

  const handleSaveCampaign = async (values) => {
    try {
      const selectedWhatsapp = whatsapps.find(
        (w) => Number(w.id) === Number(values.whatsappId)
      );
      const isCloudApiSelected = selectedWhatsapp?.type === "cloudapi";
      // SMS não tem template: a campanha é texto livre.
      const isSmsSelected = selectedWhatsapp?.type === "sms";

      // Validação de variáveis do template ANTES de salvar
      if (isCloudApiSelected && values.templateId && selectedTemplate) {
        const templateVars = extractTemplateVariables(selectedTemplate);
        const mapping = values.templateVariablesMapping || {};
        const missingVars = [];

        templateVars.forEach((varInfo) => {
          const entry = mapping[varInfo.key];
          if (!entry) {
            missingVars.push(varInfo);
          } else if (entry.type === "contact" && (!entry.field || entry.field.trim() === "")) {
            missingVars.push(varInfo);
          } else if (entry.type === "fixed" && (!entry.value || String(entry.value).trim() === "")) {
            missingVars.push(varInfo);
          }
        });

        if (missingVars.length > 0) {
          // {{1}}/{{nome}} são variáveis literais do template: ficam fora do i18n.t
          // (a interpolação do i18next apagaria as chaves duplas).
          const names = missingVars
            .map((v) => `{{${v.key}}} (${v.component}${v.buttonType ? " - " + i18n.t("campaigns.dialog.variables.buttonType", { type: v.buttonType }) : ""})`)
            .join(", ");
          toast.error(
            `${i18n.t("campaigns.dialog.toasts.missingVariables")} ${names}`
          );
          return;
        }
      }

      if (isSmsSelected) {
        if (!String(values.message || "").trim()) {
          toast.error(i18n.t("campaigns.dialog.toasts.smsMessageRequired"));
          return;
        }

        // SMS Barato não concatena segmentos: acima de 160 caracteres (já sem
        // acentos/quebras) o backend rejeita — barramos antes, com mensagem clara.
        if (selectedWhatsapp?.smsProvider === "smsbarato") {
          const sanitized = sanitizeSmsBaratoText(values.message);
          if (sanitized.length > SMSBARATO_MAX_LENGTH) {
            toast.error(
              i18n.t("campaigns.dialog.toasts.smsBaratoTooLong", {
                length: sanitized.length,
                max: SMSBARATO_MAX_LENGTH,
              })
            );
            return;
          }
        }
      } else {
        // Autor: GitHub Copilot | Data: 2026-03-28 | Motivo: Campanhas devem enviar sempre apenas template.
        if (!values.templateId) {
          toast.error(i18n.t("campaigns.dialog.toasts.templateRequired"));
          return;
        }
      }

      if (values.dispatchType === "scheduled" && !values.scheduledAt) {
        toast.error(i18n.t("campaigns.dialog.toasts.scheduledAtRequired"));
        return;
      }

      const hasResponseQueue =
        values.responseQueueId !== null &&
        values.responseQueueId !== undefined &&
        `${values.responseQueueId}` !== "";

      const responseWindow = Number(values.responseWindowMinutes);
      const hasValidResponseWindow =
        values.responseWindowMinutes !== null &&
        values.responseWindowMinutes !== undefined &&
        `${values.responseWindowMinutes}` !== "" &&
        Number.isFinite(responseWindow) &&
        responseWindow >= 1;

      if (hasResponseQueue && !hasValidResponseWindow) {
        toast.error(i18n.t("campaigns.dialog.toasts.responseWindowRequired"));
        return;
      }

      const dataValues = {};
      Object.entries(values).forEach(([key, value]) => {
        if (key === "dispatchType") {
          return;
        }
        if (key === "scheduledAt" && value !== "" && value !== null) {
          dataValues[key] = moment(value).format("YYYY-MM-DD HH:mm:ss");
        } else {
          dataValues[key] = value === "" ? null : value;
        }
      });

      if (values.dispatchType === "immediate") {
        dataValues.scheduledAt = moment().format("YYYY-MM-DD HH:mm:ss");
      }

      dataValues.status = "PROGRAMADA";

      // Garante que templateId é null se vazio
      if (!dataValues.templateId) {
        dataValues.templateId = null;
      }

      if (isSmsSelected) {
        // SMS é texto puro: sem template e sem janela de 24h.
        dataValues.sendMode = "message_or_template";
        dataValues.windowAction = "template_only";
        dataValues.templateId = null;
        dataValues.templateVariablesMapping = null;
      } else {
        // Autor: GitHub Copilot | Data: 2026-03-28 | Motivo: Padronizar envio da campanha exclusivamente via template.
        dataValues.sendMode = "template_only";
        dataValues.windowAction = "template_only";
        dataValues.message = null;
      }

      if (!dataValues.responseQueueId) {
        dataValues.responseQueueId = null;
      }

      if (!dataValues.responseWindowMinutes) {
        dataValues.responseWindowMinutes = null;
      }

      if (campaignId) {
        await api.put(`/campaigns/${campaignId}`, dataValues);

        if (attachment != null) {
          const formData = new FormData();
          formData.append("file", attachment);
          await api.post(`/campaigns/${campaignId}/media-upload`, formData);
        }
        handleClose();
      } else {
        const { data } = await api.post("/campaigns", dataValues);

        if (attachment != null) {
          const formData = new FormData();
          formData.append("file", attachment);
          await api.post(`/campaigns/${data.id}/media-upload`, formData);
        }
        if (onSave) {
          onSave(data);
        }
        handleClose();
      }
      toast.success(i18n.t("campaigns.toasts.success"));
    } catch (err) {
      console.log(err);
      toastError(err);
    }
  };

  const deleteMedia = async () => {
    if (attachment) {
      setAttachment(null);
      attachmentFile.current.value = null;
    }

    if (campaign.mediaPath) {
      await api.delete(`/campaigns/${campaign.id}/media-upload`);
      setCampaign((prev) => ({ ...prev, mediaPath: null, mediaName: null }));
      toast.success(i18n.t("campaigns.toasts.deleted"));
    }
  };

  const cancelCampaign = async () => {
    try {
      await api.post(`/campaigns/${campaign.id}/cancel`);
      toast.success(i18n.t("campaigns.toasts.cancel"));
      setCampaign((prev) => ({ ...prev, status: "CANCELADA" }));
      resetPagination();
    } catch (err) {
      toastError(err);
    }
  };

  const restartCampaign = async () => {
    try {
      await api.post(`/campaigns/${campaign.id}/restart`);
      toast.success(i18n.t("campaigns.toasts.restart"));
      setCampaign((prev) => ({ ...prev, status: "EM_ANDAMENTO" }));
      resetPagination();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div className={classes.root}>
      <ConfirmationModal
        title={i18n.t("campaigns.confirmationModal.deleteTitle")}
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onConfirm={deleteMedia}
      >
        {i18n.t("campaigns.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        scroll="paper"
      >
        <DialogTitle id="form-dialog-title">
          {campaignEditable ? (
            <>
              {campaignId
                ? `${i18n.t("campaigns.dialog.update")}`
                : `${i18n.t("campaigns.dialog.new")}`}
            </>
          ) : (
            <>{`${i18n.t("campaigns.dialog.readonly")}`}</>
          )}
        </DialogTitle>
        <div style={{ display: "none" }}>
          <input
            type="file"
            ref={attachmentFile}
            onChange={(e) => handleAttachmentFile(e)}
          />
        </div>
        <Formik
          innerRef={formikRef}
          initialValues={campaign}
          enableReinitialize={true}
          validationSchema={CampaignSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveCampaign(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ values, errors, touched, isSubmitting, setFieldValue }) => (
            (() => {
              const selectedWhatsapp = whatsapps.find(
                (w) => Number(w.id) === Number(values.whatsappId)
              );
              const isCloudApiSelected = selectedWhatsapp?.type === "cloudapi";
              // SMS é texto puro: sem template, sem janela de 24h e sem mídia.
              const isSmsSelected = selectedWhatsapp?.type === "sms";
              // Autor: GitHub Copilot | Data: 2026-03-28 | Motivo: Na criacao de campanha, exibir apenas conexoes Cloud API.
              // Canal SMS incluído: tem dispatcher próprio e também dispara campanhas.
              const availableWhatsapps = campaignId
                ? whatsapps
                : whatsapps.filter((w) => ["cloudapi", "sms"].includes(w.type));

              return (
            <Form>
              <DialogContent dividers>
                <Grid spacing={2} container>
                  {/* Nome da campanha */}
                  <Grid xs={12} item>
                    <Field
                      as={TextField}
                      label={i18n.t("campaigns.dialog.form.name")}
                      name="name"
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      className={classes.textField}
                      disabled={!campaignEditable}
                    />
                  </Grid>

                  {/* Lista de contatos */}
                  <Grid xs={12} md={4} item>
                    <FormControl
                      variant="outlined"
                      margin="dense"
                      fullWidth
                    >
                      <InputLabel id="contactList-selection-label">
                        {i18n.t("campaigns.dialog.form.contactList")}
                      </InputLabel>
                      <Field
                        as={Select}
                        label={i18n.t("campaigns.dialog.form.contactList")}
                        labelId="contactList-selection-label"
                        id="contactListId"
                        name="contactListId"
                        disabled={!campaignEditable}
                      >
                        <MenuItem value="">{i18n.t("campaigns.dialog.form.none")}</MenuItem>
                        {contactLists &&
                          contactLists.map((contactList) => (
                            <MenuItem
                              key={contactList.id}
                              value={contactList.id}
                            >
                              {contactList.name}
                            </MenuItem>
                          ))}
                      </Field>
                    </FormControl>
                  </Grid>

                  {/* Lista de tags */}
                  <Grid xs={12} md={4} item style={{ display: 'none' }}>
                    <FormControl
                      variant="outlined"
                      margin="dense"
                      fullWidth
                    >
                      <InputLabel id="tagList-selection-label">
                        {i18n.t("campaigns.dialog.form.tagList")}
                      </InputLabel>
                      <Field
                        as={Select}
                        label={i18n.t("campaigns.dialog.form.tagList")}
                        labelId="tagList-selection-label"
                        id="tagListId"
                        name="tagListId"
                        disabled={!campaignEditable}
                      >
                        <MenuItem value="">{i18n.t("campaigns.dialog.form.none")}</MenuItem>
                        {Array.isArray(tagLists) &&
                          tagLists.map((tagList) => (
                            <MenuItem key={tagList.id} value={tagList.id}>
                              {tagList.name}
                            </MenuItem>
                          ))}
                      </Field>
                    </FormControl>
                  </Grid>

                  {/* Conexão WhatsApp */}
                  <Grid xs={12} md={4} item>
                    <FormControl
                      variant="outlined"
                      margin="dense"
                      fullWidth
                    >
                      <InputLabel id="whatsapp-selection-label">
                        {i18n.t("campaigns.dialog.form.whatsappConnection")}
                      </InputLabel>
                      <Field
                        as={Select}
                        label={i18n.t("campaigns.dialog.form.whatsappConnection")}
                        labelId="whatsapp-selection-label"
                        id="whatsappId"
                        name="whatsappId"
                        disabled={!campaignEditable}
                        onChange={(e) => {
                          const newWhatsappId = e.target.value;
                          setFieldValue("whatsappId", newWhatsappId);
                          setFieldValue("templateId", "");
                          setSelectedTemplate(null);
                          setCurrentTemplateId("");
                          const conn = whatsapps.find(w => w.id === Number(newWhatsappId));

                          if (conn?.type === "cloudapi") {
                            setWabaIdForTemplates(conn?.cloudApiBusinessAccountId || "");
                            setWhatsappIdForTemplates(newWhatsappId);
                          } else {
                            setWabaIdForTemplates("");
                            setWhatsappIdForTemplates("");
                            setFieldValue("templateVariablesMapping", {});
                            setFieldValue("windowAction", "template_only");
                            setFieldValue("sendMode", "message_or_template");
                          }
                        }}
                      >
                        <MenuItem value="">{i18n.t("campaigns.dialog.form.none")}</MenuItem>
                        {availableWhatsapps &&
                          availableWhatsapps.map((whatsapp) => (
                            <MenuItem key={whatsapp.id} value={whatsapp.id}>
                              {whatsapp.name} (
                              {whatsapp.type === "cloudapi"
                                ? "Cloud API"
                                : whatsapp.type === "sms"
                                ? "SMS"
                                : "Baileys"}
                              )
                            </MenuItem>
                          ))}
                      </Field>
                    </FormControl>
                  </Grid>

                  <Grid xs={12} md={4} item>
                    <FormControl
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      error={Boolean(touched.responseQueueId && errors.responseQueueId)}
                    >
                      <InputLabel id="responseQueueId-selection-label">
                        {i18n.t("campaigns.dialog.form.responseQueue")}
                      </InputLabel>
                      <Field
                        as={Select}
                        label={i18n.t("campaigns.dialog.form.responseQueue")}
                        name="responseQueueId"
                        id="responseQueueId"
                        labelId="responseQueueId-selection-label"
                        disabled={!campaignEditable}
                      >
                        <MenuItem value="">{i18n.t("campaigns.dialog.form.none")}</MenuItem>
                        {queues.map((queue) => (
                          <MenuItem key={queue.id} value={queue.id}>
                            {queue.name}
                          </MenuItem>
                        ))}
                      </Field>
                      <FormHelperText>
                        {touched.responseQueueId && errors.responseQueueId
                          ? errors.responseQueueId
                          : i18n.t("campaigns.dialog.form.responseQueueHelper")}
                      </FormHelperText>
                    </FormControl>
                  </Grid>

                  <Grid xs={12} md={4} item>
                    <Field
                      as={TextField}
                      label={`${i18n.t("campaigns.dialog.form.responseWindow")}${values.responseQueueId ? " *" : ""}`}
                      name="responseWindowMinutes"
                      variant="outlined"
                      margin="dense"
                      type="number"
                      inputProps={{ min: 1 }}
                      fullWidth
                      className={classes.textField}
                      disabled={!campaignEditable || !values.responseQueueId}
                      error={Boolean(touched.responseWindowMinutes && errors.responseWindowMinutes)}
                      helperText={
                        touched.responseWindowMinutes && errors.responseWindowMinutes
                          ? errors.responseWindowMinutes
                          : values.responseQueueId
                            ? i18n.t("campaigns.dialog.form.responseWindowHelper")
                            : i18n.t("campaigns.dialog.form.responseWindowDisabledHelper")
                      }
                    />
                  </Grid>

                  {/* Agendamento */}
                  <Grid xs={12} md={4} item>
                    <FormControl component="fieldset" style={{ marginTop: 8 }} disabled={!campaignEditable}>
                      <FormLabel component="legend">{i18n.t("campaigns.dialog.form.dispatchType")}</FormLabel>
                      <RadioGroup
                        name="dispatchType"
                        value={values.dispatchType || "immediate"}
                        onChange={(e) => setFieldValue("dispatchType", e.target.value)}
                      >
                        <FormControlLabel
                          value="immediate"
                          control={<Radio color="primary" />}
                          label={i18n.t("campaigns.dialog.form.dispatchImmediate")}
                        />
                        <FormControlLabel
                          value="scheduled"
                          control={<Radio color="primary" />}
                          label={i18n.t("campaigns.dialog.form.dispatchScheduled")}
                        />
                      </RadioGroup>
                    </FormControl>
                  </Grid>

                  {values.dispatchType === "scheduled" && (
                  <Grid xs={12} md={4} item>
                    <Field
                      as={TextField}
                      label={i18n.t("campaigns.dialog.form.scheduledAt")}
                      name="scheduledAt"
                      error={
                        touched.scheduledAt && Boolean(errors.scheduledAt)
                      }
                      helperText={touched.scheduledAt && errors.scheduledAt}
                      variant="outlined"
                      margin="dense"
                      type="datetime-local"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                      className={classes.textField}
                      disabled={!campaignEditable}
                    />
                  </Grid>
                  )}

                  {/* Lista de arquivos */}
                  <Grid xs={12} md={4} item style={{ display: 'none' }}>
                    <FormControl
                      variant="outlined"
                      margin="dense"
                      fullWidth
                    >
                      <InputLabel id="fileListId-selection-label">
                        {i18n.t("campaigns.dialog.form.fileList")}
                      </InputLabel>
                      <Field
                        as={Select}
                        label={i18n.t("campaigns.dialog.form.fileList")}
                        name="fileListId"
                        id="fileListId"
                        labelId="fileListId-selection-label"
                        value={values.fileListId || ""}
                      >
                        <MenuItem value="">{i18n.t("common.none")}</MenuItem>
                        {Array.isArray(file) &&
                          file.map((f) => (
                            <MenuItem key={f.id} value={f.id}>
                              {f.name}
                            </MenuItem>
                          ))}
                      </Field>
                    </FormControl>
                  </Grid>

                  {isSmsSelected && (() => {
                    // SMS Barato e SMSDev convertem acentos no envio (o primeiro
                    // porque os rejeita, o segundo porque o gateway os translitera),
                    // então o contador reflete a versão já convertida. Só o Witi.me
                    // envia como digitado (contagem GSM-7/UCS-2 sobre o texto cru).
                    const smsProvider = selectedWhatsapp?.smsProvider || "witime";
                    const rawMessage = values.message || "";
                    const sanitized =
                      smsProvider === "smsbarato"
                        ? sanitizeSmsBaratoText(rawMessage)
                        : smsProvider === "smsdev"
                        ? sanitizeSmsDevText(rawMessage)
                        : rawMessage;
                    const wasConverted =
                      (smsProvider === "smsbarato" || smsProvider === "smsdev") &&
                      sanitized !== rawMessage.trim();
                    const estimate = estimateSmsSegments(sanitized);
                    const overLimit =
                      smsProvider === "smsbarato" &&
                      sanitized.length > SMSBARATO_MAX_LENGTH;

                    return (
                      <Grid xs={12} item>
                        <Divider style={{ marginTop: 8, marginBottom: 8 }} />
                        <Typography className={classes.sectionTitle}>
                          {i18n.t("campaigns.dialog.sms.sectionTitle")}
                        </Typography>
                        <Field
                          as={TextField}
                          label={i18n.t("campaigns.dialog.sms.messageLabel")}
                          name="message"
                          variant="outlined"
                          margin="dense"
                          multiline
                          minRows={4}
                          fullWidth
                          disabled={!campaignEditable}
                          error={overLimit}
                          placeholder={i18n.t("campaigns.dialog.sms.messagePlaceholder", { variable: "{nome}" })}
                          helperText={
                            smsProvider === "smsbarato"
                              ? i18n.t("campaigns.dialog.sms.helperSmsBarato", {
                                  length: sanitized.length,
                                  max: SMSBARATO_MAX_LENGTH,
                                })
                              : i18n.t("campaigns.dialog.sms.helperSegments", {
                                  length: estimate.length,
                                  encoding: estimate.encoding,
                                  segments: estimate.segments,
                                  perSegment: estimate.charsPerSegment,
                                })
                          }
                        />
                        {overLimit && (
                          <Typography variant="caption" color="error" style={{ display: "block" }}>
                            {i18n.t("campaigns.dialog.sms.overLimit", { max: SMSBARATO_MAX_LENGTH })}
                          </Typography>
                        )}
                        {wasConverted && (
                          <Typography variant="caption" color="textSecondary" style={{ display: "block" }}>
                            {i18n.t("campaigns.dialog.sms.willBeSentAs")} "{sanitized}"
                          </Typography>
                        )}
                        {smsProvider !== "smsbarato" && estimate.encoding === "UCS-2" && (
                          <Typography variant="caption" color="error">
                            {i18n.t("campaigns.dialog.sms.ucs2Warning")}
                          </Typography>
                        )}
                      </Grid>
                    );
                  })()}

                  {isCloudApiSelected && (
                    <>
                  <Grid xs={12} item>
                    <Divider style={{ marginTop: 8, marginBottom: 8 }} />
                    <Typography className={classes.sectionTitle}>
                      {i18n.t("campaigns.dialog.template.sectionTitle")}
                    </Typography>
                  </Grid>

                    <Grid xs={12} md={6} item>
                    <FormControl
                      variant="outlined"
                      margin="dense"
                      fullWidth
                    >
                      <InputLabel id="template-selection-label">
                        {i18n.t("campaigns.dialog.template.label")}
                      </InputLabel>
                      <Field
                        as={Select}
                        label={i18n.t("campaigns.dialog.template.label")}
                        labelId="template-selection-label"
                        id="templateId"
                        name="templateId"
                        disabled={
                          !campaignEditable || !values.whatsappId
                        }
                        onChange={(e) => {
                          const newTemplateId = e.target.value;
                          setFieldValue("templateId", newTemplateId);
                          setCurrentTemplateId(newTemplateId);
                        }}
                      >
                        <MenuItem value="">{i18n.t("common.none")}</MenuItem>
                        {templates.map((tmpl) => (
                          <MenuItem key={tmpl.id} value={tmpl.id}>
                            {tmpl.name} ({tmpl.language})
                          </MenuItem>
                        ))}
                      </Field>
                    </FormControl>
                    {!values.whatsappId && (
                      <Typography
                        variant="caption"
                        color="textSecondary"
                      >
                        {i18n.t("campaigns.dialog.template.selectConnection")}
                      </Typography>
                    )}
                  </Grid>

                    {/* Autor: GitHub Copilot | Data: 2026-03-28 | Motivo: Removido bloco informativo conforme solicitacao da tela. */}

                  </>
                  )}

                  {/* Preview do template selecionado */}
                  {isCloudApiSelected && selectedTemplate && (
                    <Grid xs={12} item>
                      <Box className={classes.templateInfo}>
                        <strong>{i18n.t("campaigns.dialog.template.selected")}</strong>{" "}
                        {selectedTemplate.name}
                        <br />
                        <strong>{i18n.t("campaigns.dialog.template.category")}</strong>{" "}
                        {selectedTemplate.category}
                        <br />
                        <strong>{i18n.t("campaigns.dialog.template.language")}</strong>{" "}
                        {selectedTemplate.language}
                        <br />
                        {selectedTemplate.bodyText && (
                          <>
                            <strong>{i18n.t("campaigns.dialog.template.text")}</strong>
                            <br />
                            {selectedTemplate.bodyText}
                          </>
                        )}
                      </Box>
                    </Grid>
                  )}

                  {/* Mapeamento de variáveis do template */}
                  {isCloudApiSelected && selectedTemplate && (() => {
                    const templateVars = extractTemplateVariables(selectedTemplate);
                    return templateVars.length > 0 && (
                      <Grid xs={12} item>
                        <Divider style={{ marginTop: 8, marginBottom: 8 }} />
                        <Typography className={classes.sectionTitle}>
                          {i18n.t("campaigns.dialog.variables.title")}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          style={{ marginBottom: 12 }}
                        >
                          {i18n.t("campaigns.dialog.variables.description")}
                        </Typography>
                        
                        {templateVars.map((varInfo, idx) => {
                          const currentMapping = values.templateVariablesMapping?.[varInfo.key] || { type: "contact", field: "name" };
                          
                          return (
                            <Grid container spacing={2} key={varInfo.key} style={{ marginBottom: 12 }}>
                              <Grid item xs={12} md={3}>
                                <TextField
                                  label={i18n.t("campaigns.dialog.variables.variable")}
                                  value={`{{${varInfo.key}}}`}
                                  variant="outlined"
                                  size="small"
                                  fullWidth
                                  disabled
                                  helperText={
                                    varInfo.component === "BUTTON"
                                      ? i18n.t("campaigns.dialog.variables.buttonUrlRequired", { component: varInfo.component })
                                      : varInfo.component
                                  }
                                  error={
                                    // Destaca em vermelho se não está preenchido
                                    !currentMapping ||
                                    (currentMapping.type === "fixed" && (!currentMapping.value || String(currentMapping.value).trim() === "")) ||
                                    (currentMapping.type === "contact" && (!currentMapping.field || currentMapping.field.trim() === ""))
                                  }
                                />
                              </Grid>
                              
                              <Grid item xs={12} md={3}>
                                <FormControl variant="outlined" size="small" fullWidth>
                                  <InputLabel>{i18n.t("common.type")}</InputLabel>
                                  <Select
                                    label={i18n.t("common.type")}
                                    value={currentMapping.type}
                                    onChange={(e) => {
                                      const newMapping = { ...values.templateVariablesMapping };
                                      newMapping[varInfo.key] = {
                                        type: e.target.value,
                                        field: e.target.value === "contact" ? "name" : "",
                                        value: e.target.value === "fixed" ? "" : undefined
                                      };
                                      setFieldValue("templateVariablesMapping", newMapping);
                                    }}
                                    disabled={!campaignEditable}
                                  >
                                    <MenuItem value="contact">{i18n.t("campaigns.dialog.variables.contactData")}</MenuItem>
                                    <MenuItem value="fixed">{i18n.t("campaigns.dialog.variables.fixedValue")}</MenuItem>
                                  </Select>
                                </FormControl>
                              </Grid>
                              
                              {currentMapping.type === "contact" && (
                                <Grid item xs={12} md={6}>
                                  <FormControl variant="outlined" size="small" fullWidth>
                                    <InputLabel>{i18n.t("campaigns.dialog.variables.contactField")}</InputLabel>
                                    <Select
                                      label={i18n.t("campaigns.dialog.variables.contactField")}
                                      value={currentMapping.field || "name"}
                                      onChange={(e) => {
                                        const newMapping = { ...values.templateVariablesMapping };
                                        newMapping[varInfo.key] = {
                                          type: "contact",
                                          field: e.target.value
                                        };
                                        setFieldValue("templateVariablesMapping", newMapping);
                                      }}
                                      disabled={!campaignEditable}
                                    >
                                      <MenuItem value="name">{i18n.t("common.name")}</MenuItem>
                                      <MenuItem value="number">{i18n.t("common.phone")}</MenuItem>
                                      <MenuItem value="email">{i18n.t("common.email")}</MenuItem>
                                    </Select>
                                  </FormControl>
                                </Grid>
                              )}
                              
                              {currentMapping.type === "fixed" && (
                                <Grid item xs={12} md={6}>
                                  <TextField
                                    label={i18n.t("campaigns.dialog.variables.fixedValue")}
                                    value={currentMapping.value || ""}
                                    onChange={(e) => {
                                      const newMapping = { ...values.templateVariablesMapping };
                                      newMapping[varInfo.key] = {
                                        type: "fixed",
                                        value: e.target.value
                                      };
                                      setFieldValue("templateVariablesMapping", newMapping);
                                    }}
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    placeholder={i18n.t("campaigns.dialog.variables.fixedValuePlaceholder")}
                                    disabled={!campaignEditable}
                                  />
                                </Grid>
                              )}
                            </Grid>
                          );
                        })}
                      </Grid>
                    );
                  })()}

                  {/* Anexo */}
                  {(campaign.mediaPath || attachment) && (
                    <Grid xs={12} item>
                      <Button startIcon={<AttachFileIcon />}>
                        {attachment != null
                          ? attachment.name
                          : campaign.mediaName}
                      </Button>
                      {campaignEditable && (
                        <IconButton
                          onClick={() => setConfirmationOpen(true)}
                          color="secondary"
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      )}
                    </Grid>
                  )}
                </Grid>
              </DialogContent>
              <DialogActions>
                {campaign.status === "CANCELADA" && (
                  <Button
                    color="primary"
                    onClick={() => restartCampaign()}
                    variant="outlined"
                  >
                    {i18n.t("campaigns.dialog.buttons.restart")}
                  </Button>
                )}
                {campaign.status === "EM_ANDAMENTO" && (
                  <Button
                    color="primary"
                    onClick={() => cancelCampaign()}
                    variant="outlined"
                  >
                    {i18n.t("campaigns.dialog.buttons.cancel")}
                  </Button>
                )}
                {/* Autor: GitHub Copilot | Data: 2026-03-28 | Motivo: Removido botao de anexo conforme regra da campanha. */}
                <Button
                  onClick={handleClose}
                  color="secondary"
                  disabled={isSubmitting}
                  variant="outlined"
                >
                  {i18n.t("campaigns.dialog.buttons.close")}
                </Button>
                {(campaignEditable || campaign.status === "CANCELADA") && (
                  <Button
                    type="submit"
                    color="primary"
                    disabled={isSubmitting}
                    variant="contained"
                    className={classes.btnWrapper}
                  >
                    {campaignId
                      ? `${i18n.t("campaigns.dialog.buttons.edit")}`
                      : `${i18n.t("campaigns.dialog.buttons.add")}`}
                    {isSubmitting && (
                      <CircularProgress
                        size={24}
                        className={classes.buttonProgress}
                      />
                    )}
                  </Button>
                )}
              </DialogActions>
            </Form>
              );
            })()
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default CampaignModal;
